import { useCallback } from 'react';
import useSWR from 'swr';

import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import {
  taskStatusesArchiveCreate,
  taskStatusesCreate,
  taskStatusesList,
  taskStatusesPartialUpdate,
  taskStatusesReorderCreate,
} from '~/data/generated/endpoints/task-statuses/task-statuses';
import type {
  PatchedTaskStatusUpdateRequest,
  TaskStatusCreateRequest,
  TaskStatusWithCount,
} from '~/data/generated/model';
import { TaskStatusesListResponse } from '~/data/generated/zod/task-statuses/task-statuses.zod';
import { TASK_STATUSES_KEY, useRevalidateTasks } from '~/data/hooks/taskKeys';
import { parseApiResponse } from '~/data/parseApiResponse';

export function useTaskStatuses() {
  const enabled = useTasksEnabled();
  const { data, error, isLoading, mutate } = useSWR(enabled ? TASK_STATUSES_KEY : null, async () =>
    parseApiResponse(TaskStatusesListResponse, (await taskStatusesList()).data, 'TaskStatus'),
  );
  return { statuses: data ?? [], error, isLoading, mutate };
}

export function useTaskStatusActions() {
  const { mutate } = useTaskStatuses();
  const revalidateTasks = useRevalidateTasks();

  const run = useCallback(
    async (write: () => Promise<{ data: TaskStatusWithCount[] }>) => {
      const { data } = await write();
      await mutate(data, { revalidate: false });
      await revalidateTasks();
      return data;
    },
    [mutate, revalidateTasks],
  );

  return {
    addStatus: useCallback(
      (body: TaskStatusCreateRequest) => run(() => taskStatusesCreate(body)),
      [run],
    ),
    updateStatus: useCallback(
      (id: string, body: PatchedTaskStatusUpdateRequest) =>
        run(() => taskStatusesPartialUpdate(id, body)),
      [run],
    ),
    reorderStatuses: useCallback(
      (ids: string[]) => run(() => taskStatusesReorderCreate({ ids })),
      [run],
    ),
    archiveStatus: useCallback(
      (id: string, moveToId: string | null) =>
        run(() => taskStatusesArchiveCreate(id, { moveToId })),
      [run],
    ),
  };
}
